// Client-side encryption for portable .wat files.
// Standard authenticated encryption: PBKDF2-SHA256 → AES-256-GCM.
// The passphrase is NEVER stored, logged, or sent anywhere — it stays in this
// function's scope only long enough to derive the key. A wrong passphrase or a
// damaged/tampered file fails the GCM auth check, so nothing is ever imported
// partially.

export const WAT_FORMAT = "wat.encrypted";
export const WAT_VERSION = 1;
const ITERATIONS = 210000;

const ENC = new TextEncoder();
const DEC = new TextDecoder();

export class WatError extends Error {}

const b64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};
const unb64 = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));

async function deriveKey(passphrase, salt, iterations) {
  const base = await crypto.subtle.importKey(
    "raw",
    ENC.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

// Additional authenticated data binds the header so metadata can't be tampered.
const aadOf = (h) =>
  ENC.encode(
    JSON.stringify({
      format: h.format,
      version: h.version,
      kdf: h.kdf,
      iterations: h.iterations,
      cipher: h.cipher,
      salt: h.salt,
      iv: h.iv,
    })
  );

export async function encryptWat(payloadObj, passphrase) {
  if (!passphrase) throw new WatError("A passphrase is required.");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt, ITERATIONS);
  const header = {
    format: WAT_FORMAT,
    version: WAT_VERSION,
    kdf: "PBKDF2-SHA256",
    iterations: ITERATIONS,
    cipher: "AES-256-GCM",
    salt: b64(salt),
    iv: b64(iv),
  };
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: aadOf(header) },
    key,
    ENC.encode(JSON.stringify(payloadObj))
  );
  return JSON.stringify({ ...header, ciphertext: b64(ct) }, null, 2);
}

export async function decryptWat(fileText, passphrase) {
  let c;
  try {
    c = JSON.parse(fileText);
  } catch {
    throw new WatError("This isn't a valid .wat file.");
  }
  if (!c || c.format !== WAT_FORMAT) throw new WatError("Unrecognized file format.");
  if (typeof c.version !== "number") throw new WatError("This .wat file is missing its version.");
  if (c.version > WAT_VERSION) {
    throw new WatError(
      `This file was created by a newer version of WAT (v${c.version}). Please update to open it.`
    );
  }
  let salt;
  let iv;
  let ctBytes;
  try {
    salt = unb64(c.salt);
    iv = unb64(c.iv);
    ctBytes = unb64(c.ciphertext);
  } catch {
    throw new WatError("This .wat file is damaged. Nothing was imported.");
  }
  const iterations = Number(c.iterations) || ITERATIONS;
  const key = await deriveKey(passphrase, salt, iterations);
  let pt;
  try {
    pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, additionalData: aadOf({ ...c, iterations }) },
      key,
      ctBytes
    );
  } catch {
    // GCM authentication failed → wrong passphrase OR corrupted/tampered file.
    throw new WatError("Wrong passphrase, or the file is damaged. Nothing was imported.");
  }
  try {
    return JSON.parse(DEC.decode(pt));
  } catch {
    throw new WatError("The decrypted data was unreadable. Nothing was imported.");
  }
}
